/**
 * `<SettingsRow>` — THE standard layout for a single setting inside a
 * `<SettingsCard>` (U4, 2026-07-04).
 *
 * Label + optional hint on the left, the control slot (children) on the
 * right, with consistent vertical rhythm (hairline between adjacent rows —
 * see settings.css `.settings-row`). Use `stack` for wide controls
 * (textareas, tables, option grids) that should drop below the label
 * full-width instead of sitting beside it.
 *
 * New Settings panels should compose this instead of ad-hoc `.col`/`.row`
 * divs or inline `style={{ gap }}` layout:
 *
 *   <SettingsRow htmlFor="my-input" label="Routing prompt" hint="What it does">
 *     <select id="my-input" className="settings-select">…</select>
 *   </SettingsRow>
 */

import type { ReactNode } from 'react';

interface SettingsRowProps {
  /** When set, the label renders as `<label htmlFor>` for a11y pairing with
   *  the control's `id`. Omit for non-labelable controls (button groups). */
  htmlFor?: string;
  label: ReactNode;
  /** Optional one-liner under the label in --fg-3. */
  hint?: ReactNode;
  /** Stack the control full-width below the label (wide controls). */
  stack?: boolean;
  /** The control slot. */
  children: ReactNode;
}

export function SettingsRow({
  htmlFor,
  label,
  hint,
  stack = false,
  children,
}: SettingsRowProps): JSX.Element {
  return (
    <div className={stack ? 'settings-row settings-row--stack' : 'settings-row'}>
      <div className="settings-row__text">
        {htmlFor !== undefined ? (
          <label className="settings-row__label" htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <div className="settings-row__label">{label}</div>
        )}
        {hint !== undefined && <div className="settings-row__hint">{hint}</div>}
      </div>
      <div className="settings-row__control">{children}</div>
    </div>
  );
}
